// The engine's values as the page sees them: the field names of interplae-align-session's Rust
// types, which interplae-align-wasm hands over through serde-wasm-bindgen. A missing value is null.

export type Column = 'Source' | 'Target'

/// Which way cells move (align_session::Direction).
export type Direction = 'Up' | 'Down'

export interface PieceView {
  /// The piece's display text, or a line of the edited text. A caret offset counts its UTF-16 units.
  text: string
  /// The paragraph it comes from; null for a line of edited text.
  paragraph: number | null
  heading: boolean
  note: boolean
}

export interface CellView {
  /// None for a blank cell. Paragraph breaks fall between pieces.
  pieces: PieceView[]
  edited: boolean
  /// The document's display text under an edited cell, paragraph breaks as \n.
  original: string | null
}

export interface Flags {
  low_score: boolean
  numbers: boolean
  one_sided: boolean
  length: boolean
  edited: boolean
  crosses_paragraph: boolean
  /// The review with Claude changed the row or left a warning on it (brief 48).
  reviewed: boolean
}

/// The review's mark on a row: whether it changed the row, and the model's warning on it.
export interface Reviewed {
  changed: boolean
  warning: string | null
}

export interface RowView {
  source: CellView
  target: CellView
  locked: boolean
  excluded: boolean
  flags: Flags
  /// The sentence stage's cost of a two-sided row in nats; lower is more certain.
  score: number | null
  review: Reviewed | null
}

/// `removed` rows from `at` were replaced by `inserted` rows; no other row changed.
export interface Change {
  at: number
  removed: number
  inserted: number
}

export interface Counts {
  rows: number
  two_sided: number
  one_sided: number
  locked: number
  excluded: number
  flagged: number
}

export interface Exported {
  xml: string
  paired: number
  one_sided: number
  kept_one_sided: boolean
}

export const cellOf = (row: RowView, column: Column): CellView => (column === 'Source' ? row.source : row.target)

export const anyFlag = (f: Flags): boolean => f.low_score || f.numbers || f.one_sided || f.length || f.edited || f.crosses_paragraph || f.reviewed

/// Each flag in words, in the order the page lists them.
export const FLAG_WORDS: [keyof Flags, string][] = [
  ['one_sided', 'one-sided'],
  ['numbers', 'numbers differ'],
  ['length', 'odd length'],
  ['low_score', 'low score'],
  ['crosses_paragraph', 'crosses a paragraph break'],
  ['edited', 'text edited'],
  ['reviewed', 'changed by Claude'],
]

/// A row's flags in words. The review's flag carries the model's warning as its text.
export function flagWords(view: RowView): string[] {
  return FLAG_WORDS.filter(([k]) => view.flags[k]).map(([k, w]) => {
    if (k !== 'reviewed' || !view.review) return w
    const { changed, warning } = view.review
    if (!warning) return w
    return changed ? `changed by Claude, who warns: ${warning}` : `Claude warns: ${warning}`
  })
}

/// What a review is about to send: interplae-align-llm's Estimate.
export interface Estimate {
  windows: number
  input_tokens: number
  output_tokens: number
  cost_usd: number | null
  model: string
  /// The host the requests go to.
  host: string
}

export interface ReviewProgress {
  done: number
  total: number
  failed: number
}

/// A request the review's engine built, with the key's slot for the sender to fill
/// (interplae-machine's HttpRequest).
export interface ReviewRequest {
  method: string
  url: string
  headers: [string, string][]
  body: string
  credential: { kind: 'header'; name: string; prefix: string } | { kind: 'query'; name: string } | { kind: 'body'; pointer: string } | null
  timeout_ms: number
}

/// What came back, as interplae-machine's HttpResponse reads it: status 0 with a failure when
/// nothing answered.
export interface ReviewResponse {
  status: number
  headers: [string, string][]
  body: string
  failure: { kind: 'unreachable'; message: string } | { kind: 'timeout' } | null
}

/// What a review did (interplae-align-session's ReviewDone).
export interface ReviewDone {
  change: Change
  changed: number
  failed: { first: number; last: number; reason: string }[]
  input_tokens: number
  output_tokens: number
  stopped: string | null
}
